/**
 * Sends form submissions through EmailJS's REST endpoint.
 *
 * This used to import the @emailjs/browser SDK, which reads `localStorage`
 * while the module loads. In Safari with cookies and site data blocked, merely
 * reading `localStorage` throws "SecurityError: The operation is insecure", so
 * the chunk holding every email form failed to load (PostHog error tracking,
 * Aug 30 – Sep 3 2026, Mobile Safari). The SDK's send() is a single JSON POST
 * to /api/v1.0/email/send; this makes the same request with nothing at module
 * scope that can throw. The request body matches SDK 4.4.1 field for field.
 */

const EMAILJS_ENDPOINT = 'https://api.emailjs.com/api/v1.0/email/send'

const publicKey = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY
const serviceId = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID
const templateId = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID

type FormType = 'contact_form' | 'android_waitlist' | 'detox_challenge'

interface EmailParams {
  [key: string]: unknown
  from_name: string
  reply_to: string
  subject: string
  message: string
  type?: FormType
}

export interface EmailResult {
  success: boolean
  response?: { status: number; text: string }
  error?: unknown
}

/** Waitlist and detox sign-ups only need the address, in a fixed message. */
function templateParamsFor(params: EmailParams): EmailParams {
  const name = params.from_name || ''
  const who = `${params.reply_to}${name ? ` (${name})` : ''}`
  if (params.type === 'android_waitlist') {
    return {
      reply_to: params.reply_to,
      from_name: name,
      subject: 'Android Waitlist Signup',
      message: `New signup for Android waitlist: ${who}`,
      type: params.type,
    }
  }
  if (params.type === 'detox_challenge') {
    return {
      reply_to: params.reply_to,
      from_name: name,
      subject: '7-Day Microplastic Detox Challenge Signup',
      message: `New signup for 7-day detox challenge: ${who}`,
      type: params.type,
    }
  }
  return params
}

export const sendEmail = async (params: EmailParams): Promise<EmailResult> => {
  if (!publicKey || !serviceId || !templateId) {
    return { success: false, error: new Error('EmailJS is not configured') }
  }
  try {
    const res = await fetch(EMAILJS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        lib_version: '4.4.1',
        user_id: publicKey,
        service_id: serviceId,
        template_id: templateId,
        template_params: templateParamsFor(params),
      }),
    })
    const text = await res.text()
    const response = { status: res.status, text }
    if (res.ok) return { success: true, response }
    // Log the status only: the request carries the visitor's email address.
    console.error('Email send failed with status', res.status)
    return { success: false, response, error: new Error(`EmailJS ${res.status}`) }
  } catch (error) {
    console.error('Email send failed: network error')
    return { success: false, error }
  }
}
